<template>
  <div class="button-container">
    <button v-if="finished_playing" @click="show" class="load-button">Ver ranking</button>
  </div>
  <div v-if="isVisible" class="ranking-popup-overlay" @click="closePopup">
    <div class="ranking-popup" @click.stop>
      <div class="ranking-header">
        <h2>Ranking del puzzle #{{ currentPuzzleNb }}</h2>
        <div class="puzzle-selector">
          <button @click="previousPuzzle" :disabled="!canGoPrevious" class="puzzle-button">
            ←
          </button>
          <button @click="nextPuzzle" :disabled="!canGoNext" class="puzzle-button">→</button>
        </div>
        <button class="close-button" @click="closePopup">×</button>
      </div>
      <div class="streaks-info" v-if="currentUserId">
        <div class="streak-item">
          <span class="streak-label">Racha de partidas</span>
          <span class="streak-value">{{ playStreak }}</span>
        </div>
        <div class="streak-item">
          <span class="streak-label">Racha de victorias</span>
          <span class="streak-value">{{ winStreak }}</span>
        </div>
      </div>
      <div class="ranking-content">
        <div v-if="loading" class="loading">Loading ranking...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="ranking.length === 0" class="no-results">
          No results yet for today's puzzle
        </div>
        <table v-else class="ranking-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Score</th>
              <th>Pistas usadas</th>
              <th>Tiempo</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in ranking"
              :key="entry.position"
              :class="{ 'current-user': entry.ismy }"
            >
              <td>{{ entry.position }}</td>
              <td>{{ entry.is_anonymous ? 'Anonymous' : entry.userId }}</td>
              <td>{{ entry.final_score }}</td>
              <td>{{ entry.hints_used }}</td>
              <td>{{ formatTime(entry.time_spent) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />

      <h3>Ranking</h3>

      El orden se establece:
      <ul>
        <li>Primero, por mayor puntuación (score).</li>
        <li>En caso de empate, por menor cantidad de pistas usadas (pistas).</li>
        <li>Si persiste el empate, por menor tiempo invertido (tiempo).</li>
      </ul>

      En caso de derrota, el score es negativo y corresponde al numero de letras sin adivinar (sin
      contar duplicados por simetría).

      <h3>Rachas</h3>

      <ul>
        <li>
          Racha de partidas: cantidad de partidas consecutivas completadas, sin importar si se gana
          o se pierde.
        </li>
        <li>Racha de victorias: número de partidas consecutivas ganadas (score positivo).</li>
      </ul>

      <h3>Modo infinito</h3>
      <ul>
        <li>
          Los resultados en <b>modo infinito</b> solo serán visibles para el propio jugador y
          aparecerán siempre al final del ranking.
        </li>
        <li>
          Las partidas en modo infinito cuentan para la <b>racha de partidas</b>, pero
          <b>no</b> para la <b>racha de victorias</b> (independientemente del score).
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { config } from '../../config'

export default {
  name: 'RankingPopup',
  props: {
    puzzle_nb: {
      type: Number,
      required: true,
    },
    finished_playing: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isVisible: false,
      loading: false,
      error: null,
      ranking: [],
      currentPuzzleNb: null,
      currentUserId: null,
      playStreak: 0,
      winStreak: 0,
    }
  },
  computed: {
    canGoPrevious() {
      return this.currentPuzzleNb > 1
    },
    canGoNext() {
      return this.currentPuzzleNb < this.puzzle_nb
    },
  },
  methods: {
    async show() {
      this.isVisible = true
      this.currentPuzzleNb = this.puzzle_nb
      await this.fetchRanking()
      await this.fetchStreaks()
    },
    closePopup() {
      this.isVisible = false
    },
    async previousPuzzle() {
      if (this.canGoPrevious) {
        this.currentPuzzleNb--
        await this.fetchRanking()
      }
    },
    async nextPuzzle() {
      if (this.canGoNext) {
        this.currentPuzzleNb++
        await this.fetchRanking()
      }
    },
    async fetchRanking() {
      this.loading = true
      this.error = null
      try {
        // Get user id
        const auth_data_raw = localStorage.getItem('auth_data')
        const auth_data = auth_data_raw ? JSON.parse(auth_data_raw) : null

        // if logged in, get username from auth_data
        if (auth_data !== null) {
          try {
            this.currentUserId = auth_data.user.username
          } catch (err) {
            console.error('User authenticated but no username found:', err)
          }
        }

        // if not logged in, get anonymous id from anonymous_id
        if (this.currentUserId == null) {
          this.currentUserId = localStorage.getItem('anonymous_id')
        }

        if (this.currentUserId == null) {
          console.error('Any user_id found. This should never happend...')
        }

        const response = await fetch(
          `${config.getBackendUrl()}/game/ranking/${this.currentPuzzleNb}/${this.currentUserId}`,
        )
        const data = await response.json()
        this.ranking = data.ranking

        // // Filter out infinite mode players except for the current user
        // if (this.currentUserId) {
        //   this.ranking = this.ranking.filter(rank => rank.userId === this.currentUserId || !rank.infinite_mode);
        // }

        // console.log("ranking 1", this.ranking);

        // // Move infinite_mode results to the end of the ranking
        // if (this.ranking && this.ranking.length > 0) {
        //   this.ranking = [
        //     ...this.ranking.filter(rank => !rank.infinite_mode),
        //     ...this.ranking.filter(rank => rank.infinite_mode)
        //   ];
        // }

        // console.log("ranking 2", this.ranking);

        const userRank = this.ranking.find((rank) => rank.userId === this.currentUserId)
        if (userRank) {
          userRank.ismy = true
        } else {
          console.log('No user id found')
        }
      } catch (err) {
        this.error = 'Failed to load ranking'
        console.error('Error fetching ranking:', err)
      } finally {
        this.loading = false
      }
    },
    async fetchStreaks() {
      if (this.currentUserId) {
        try {
          const response = await fetch(
            `${config.getBackendUrl()}/game/streaks/${this.currentUserId}`,
          )
          const data = await response.json()
          this.playStreak = data.play_streak
          this.winStreak = data.win_streak
        } catch (err) {
          console.error('Error fetching streaks:', err)
        }
      }
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },
  },
}
</script>

<style scoped>
.ranking-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.ranking-popup {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 80%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.ranking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0 8px;
}

.ranking-table {
  width: 100%;
  border-collapse: collapse;
}

.ranking-table th,
.ranking-table td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.ranking-table th {
  background-color: #f5f5f5;
}

.ranking-table tr:hover {
  background-color: #f9f9f9;
}

.ranking-table tr.current-user {
  background-color: #e6f7ff;
  font-weight: bold;
}

.ranking-table tr:nth-child(1) {
  background-color: rgba(255, 215, 0, 0.5); /* Gold */
}

.ranking-table tr:nth-child(2) {
  background-color: rgba(192, 192, 192, 0.5); /* Silver */
}

.ranking-table tr:nth-child(3) {
  background-color: rgba(205, 127, 50, 0.5); /* Bronze */
}

.ranking-table tr:nth-child(1):hover,
.ranking-table tr:nth-child(2):hover,
.ranking-table tr:nth-child(3):hover {
  opacity: 0.7;
}

.loading,
.error,
.no-results {
  text-align: center;
  padding: 20px;
}

.error {
  color: red;
}

.button-container {
  margin: 15px;
  display: flex;
  justify-content: center;
  width: 100%;
}

.load-button {
  background-color: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.load-button:hover {
  background-color: #2980b9;
}

.puzzle-selector {
  display: flex;
  align-items: center;
  gap: 10px;
}

.puzzle-button {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 16px;
}

.puzzle-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.streaks-info {
  margin: 15px 0;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
  display: flex;
  justify-content: space-around;
}

.streak-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.streak-label {
  font-size: 0.9em;
  color: #666;
}

.streak-value {
  font-size: 1.2em;
  font-weight: bold;
  color: #3498db;
}
</style>
